import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { type Lead, type LeadTemplate, type VariableContext } from "../../lib/lead-types.ts";
import { interpolateLeadTemplate, LEAD_VARIABLE_TOKENS, getUnfilledVariables } from "../../lib/lead-templates.ts";
import { getAllLeadTemplates, recordLeadEmailSent } from "../../lib/lead-storage.ts";
import { sendOutreachEmail, SENDER_OPTIONS, DEFAULT_SENDER_EMAIL } from "../../lib/email-service.ts";
import { useSession } from "../../lib/auth-client.ts";
import { toast } from "../Toast.tsx";

interface Props {
  leads: Lead[];
  selectedLeadId?: string;
  onLeadChange: (leadId: string) => void;
  onEmailSent: () => void;
  onNavigateToDirectory: () => void;
}

export function LeadOutreachComposer({
  leads,
  selectedLeadId,
  onLeadChange,
  onEmailSent,
  onNavigateToDirectory,
}: Props) {
  const { data: session } = useSession();
  const [searchParams] = useSearchParams();
  const urlTemplateId = searchParams.get("templateId");
  const templates = getAllLeadTemplates();

  const selectedLead = leads.find((l) => l.id === selectedLeadId) || leads[0] || null;

  // Selected sender address
  const [senderEmail, setSenderEmail] = useState<string>(DEFAULT_SENDER_EMAIL);

  // Selected template
  const [templateId, setTemplateId] = useState<string>(
    urlTemplateId || (
      selectedLead && (selectedLead.followUpCount ?? 0) > 0
        ? "lead_followup_bump"
        : "lead_expense_management"
    ),
  );

  useEffect(() => {
    if (urlTemplateId) {
      setTemplateId(urlTemplateId);
    }
  }, [urlTemplateId]);

  // Variable fields
  const [company, setCompany] = useState("");
  const [contactName, setContactName] = useState("");
  const [website, setWebsite] = useState("");
  const [useCase, setUseCase] = useState("");
  const [painPoint, setPainPoint] = useState("");
  const [senderName, setSenderName] = useState(session?.user.name || "Jayanth");

  // Editable Subject and Body
  const [subject, setSubject] = useState("");
  const [bodyContent, setBodyContent] = useState("");
  const [previewMode, setPreviewMode] = useState<"rendered" | "text">("rendered");

  // Sending state
  const [sending, setSending] = useState(false);
  const bodyTextareaRef = useRef<HTMLTextAreaElement>(null);

  // When selected lead changes, populate variables and template
  useEffect(() => {
    if (selectedLead) {
      setCompany(selectedLead.company || "");
      setContactName(selectedLead.contactName || "");
      setWebsite(selectedLead.website || "");
      setUseCase(selectedLead.useCase || "real-time currency conversion and historical rates");
      setPainPoint(selectedLead.painPoint || "volatile FX spreads and manual reconciliation");

      // Auto pick appropriate template if switching between new and already contacted leads
      if ((selectedLead.followUpCount ?? 0) > 0) {
        setTemplateId("lead_followup_bump");
      } else if (selectedLead.category === "expense_management") {
        setTemplateId("lead_expense_management");
      } else if (selectedLead.category === "saas_pricing") {
        setTemplateId("lead_saas_pricing");
      } else if (selectedLead.category === "invoicing_payroll") {
        setTemplateId("lead_invoicing_payroll");
      } else if (selectedLead.category === "ecommerce") {
        setTemplateId("lead_ecommerce");
      }
    }
  }, [selectedLead?.id]);

  // When templateId changes, update subject and body
  useEffect(() => {
    const tpl = templates.find((t) => t.id === templateId);
    if (tpl) {
      setSubject(tpl.subject);
      setBodyContent(tpl.htmlBody);
    }
  }, [templateId]);

  // Current variable context
  const variableContext: VariableContext = {
    company: company.trim() || "{{company}}",
    name: contactName.trim() || "there",
    website: website.trim() || "{{website}}",
    use_case: useCase.trim() || "{{use_case}}",
    pain_point: painPoint.trim() || "{{pain_point}}",
    sender_name: senderName.trim() || "CurrencyCore Team",
    email: selectedLead?.email || "{{email}}",
  };

  // Interpolated preview strings
  const interpolatedSubject = interpolateLeadTemplate(subject, variableContext);
  const interpolatedHtml = interpolateLeadTemplate(bodyContent, variableContext);
  const interpolatedText = interpolatedHtml
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<li>/gi, "- ")
    .replace(/<\/li>/gi, "\n")
    .replace(/<[^>]+>/g, "");

  // Check for any remaining unfilled variables
  const unfilledSubject = getUnfilledVariables(interpolatedSubject);
  const unfilledBody = getUnfilledVariables(interpolatedHtml);
  const allUnfilled = Array.from(new Set([...unfilledSubject, ...unfilledBody]));

  // Insert token at cursor in body textarea
  function insertVariableToken(token: string) {
    const textarea = bodyTextareaRef.current;
    if (!textarea) {
      setBodyContent((prev) => prev + " " + token);
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = bodyContent;
    const before = text.substring(0, start);
    const after = text.substring(end);
    const newText = before + token + after;
    setBodyContent(newText);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + token.length, start + token.length);
    }, 0);
  }

  async function handleSendEmail() {
    if (!selectedLead) {
      toast("Please select a lead first.");
      return;
    }
    if (!selectedLead.email) {
      toast("Selected lead has no email address configured.");
      return;
    }
    if (!interpolatedSubject.trim()) {
      toast("Subject line cannot be empty.");
      return;
    }
    if (!interpolatedHtml.trim()) {
      toast("Email body content cannot be empty.");
      return;
    }

    if (allUnfilled.length > 0) {
      const proceed = window.confirm(
        `Warning: There are still unfilled variables (${allUnfilled.join(", ")}). Do you want to send anyway?`,
      );
      if (!proceed) return;
    }

    const tpl = templates.find((t) => t.id === templateId);
    const templateName = tpl?.name || "Custom Outreach";

    setSending(true);
    try {
      // 1. Dispatch outreach email (1-by-1)
      await sendOutreachEmail({
        fromEmail: senderEmail,
        recipients: [
          {
            email: selectedLead.email,
            name: contactName.trim() || undefined,
            subject: interpolatedSubject,
            html: interpolatedHtml,
            text: interpolatedText,
          },
        ],
        subject: interpolatedSubject,
        html: interpolatedHtml,
        text: interpolatedText,
        templateId,
        templateName,
      });

      // 2. Record to lead history & advance status
      recordLeadEmailSent(selectedLead.id, {
        leadId: selectedLead.id,
        companyName: selectedLead.company,
        recipientEmail: selectedLead.email,
        recipientName: contactName.trim() || null,
        senderEmail: senderEmail,
        templateId,
        templateName,
        subject: interpolatedSubject,
        htmlContent: interpolatedHtml,
        status: "sent",
      });

      toast(`Outreach email sent successfully to ${selectedLead.email}!`);
      onEmailSent();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to send email.";
      toast(msg);

      recordLeadEmailSent(selectedLead.id, {
        leadId: selectedLead.id,
        companyName: selectedLead.company,
        recipientEmail: selectedLead.email,
        recipientName: contactName.trim() || null,
        senderEmail: senderEmail,
        templateId,
        templateName,
        subject: interpolatedSubject,
        htmlContent: interpolatedHtml,
        status: "failed",
        error: msg,
      });
    } finally {
      setSending(false);
    }
  }

  if (leads.length === 0) {
    return (
      <div className="card text-center py-10">
        <h3>No Leads Available</h3>
        <p className="muted mt-2">
          Add prospective leads in the Directory tab first before composing outreach emails.
        </p>
        <button
          type="button"
          className="btn btn-primary mt-4"
          onClick={onNavigateToDirectory}
        >
          Go to Leads Directory
        </button>
      </div>
    );
  }

  return (
    <div className="lead-composer-layout">
      {/* Left Column: Lead selection, variables, and editable content */}
      <div className="lead-composer-config-pane">
        <div className="card">
          <div className="card-header border-b pb-3 mb-4">
            <h4>1. Target Prospect &amp; Template</h4>
          </div>

          <div className="form-group mb-3">
            <label className="form-label font-semibold">Select Lead (1-by-1 Outreach)</label>
            <select
              className="select w-full"
              value={selectedLead?.id || ""}
              onChange={(e) => onLeadChange(e.target.value)}
            >
              {leads.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.company} &lt;{l.email}&gt; ({l.status}
                  {(l.followUpCount ?? 0) > 0 ? `, ${l.followUpCount} sent` : ""})
                </option>
              ))}
            </select>
          </div>

          {selectedLead && (
            <div className="lead-quick-status-bar mb-4">
              <span className="lead-status-pill">{selectedLead.status}</span>
              <span className="muted text-xs">
                {(selectedLead.followUpCount ?? 0) === 0
                  ? "Initial outreach"
                  : `Follow-up #${selectedLead.followUpCount} (last emailed ${
                      selectedLead.lastContactedAt
                        ? new Date(selectedLead.lastContactedAt).toLocaleDateString()
                        : "recently"
                    })`}
              </span>
              {selectedLead.website && (
                <a
                  href={selectedLead.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="lead-link-ext text-xs ml-auto"
                >
                  Visit site ↗
                </a>
              )}
            </div>
          )}

          <div className="form-group mb-3">
            <label className="form-label font-semibold">Sender Email Address</label>
            <select
              className="select w-full"
              value={senderEmail}
              onChange={(e) => setSenderEmail(e.target.value)}
            >
              {SENDER_OPTIONS.map((opt) => (
                <option key={opt.email} value={opt.email}>
                  {opt.label}
                </option>
              ))}
            </select>
            <span className="form-hint">
              Replies from {selectedLead?.company || "lead"} route directly to this address.
            </span>
          </div>

          <div className="form-group mb-4">
            <label className="form-label font-semibold">Email Template</label>
            <select
              className="select w-full"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
            >
              <optgroup label="Initial Outreach Templates">
                {templates
                  .filter((t) => !t.isFollowUp)
                  .map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
              </optgroup>
              <optgroup label="Follow-Up Templates">
                {templates
                  .filter((t) => t.isFollowUp)
                  .map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
              </optgroup>
            </select>
          </div>

          <div className="border-t pt-3 mb-4">
            <label className="form-label font-semibold mb-2 block">
              2. Variable Fields (Auto-filled from lead)
            </label>
            <div className="grid-2col gap-2">
              <div>
                <label className="text-xs muted block mb-1">Company Name</label>
                <input
                  type="text"
                  className="input input-sm w-full"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="e.g. SmartSpend"
                />
              </div>
              <div>
                <label className="text-xs muted block mb-1">Contact Name</label>
                <input
                  type="text"
                  className="input input-sm w-full"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder="e.g. Alex"
                />
              </div>
              <div>
                <label className="text-xs muted block mb-1">Website URL</label>
                <input
                  type="text"
                  className="input input-sm w-full"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="e.g. smartspend.app"
                />
              </div>
              <div>
                <label className="text-xs muted block mb-1">Sender Name</label>
                <input
                  type="text"
                  className="input input-sm w-full"
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  placeholder="e.g. Jayanth"
                />
              </div>
            </div>

            <div className="mt-2">
              <label className="text-xs muted block mb-1">Use Case / Feature Focus</label>
              <input
                type="text"
                className="input input-sm w-full"
                value={useCase}
                onChange={(e) => setUseCase(e.target.value)}
                placeholder="e.g. Multi-currency receipt reconciliation & corporate card rates"
              />
            </div>

            <div className="mt-2">
              <label className="text-xs muted block mb-1">Specific Pain Point</label>
              <input
                type="text"
                className="input input-sm w-full"
                value={painPoint}
                onChange={(e) => setPainPoint(e.target.value)}
                placeholder="e.g. Volatile FX spreads and delayed daily average rates"
              />
            </div>
          </div>

          <div className="border-t pt-3 mb-2">
            <div className="flex-between mb-2">
              <label className="form-label font-semibold">3. Content Editor</label>
              <span className="muted text-xs">Variables insertable at cursor</span>
            </div>

            {/* Variable Inserter Chips */}
            <div className="variable-chips-bar mb-3">
              <span className="text-xs muted mr-1">Insert:</span>
              {LEAD_VARIABLE_TOKENS.map((token) => (
                <button
                  key={token.token}
                  type="button"
                  className="btn-chip"
                  title={token.description}
                  onClick={() => insertVariableToken(token.token)}
                >
                  + {token.token}
                </button>
              ))}
            </div>

            <div className="form-group mb-3">
              <label className="form-label text-xs">Subject Line (Editable)</label>
              <input
                type="text"
                className="input w-full"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Email Subject"
              />
            </div>

            <div className="form-group">
              <label className="form-label text-xs">Email Body (HTML/Text Editable)</label>
              <textarea
                ref={bodyTextareaRef}
                className="textarea w-full font-mono text-xs"
                rows={12}
                value={bodyContent}
                onChange={(e) => setBodyContent(e.target.value)}
                placeholder="Type email body..."
              />
            </div>
          </div>
        </div>
      </div>

      {/* Right Column: Live Rendered Preview & Send Action */}
      <div className="lead-composer-preview-pane">
        <div className="card sticky-preview">
          <div className="preview-top-toolbar">
            <div>
              <h4>Live Rendered Preview</h4>
              <p className="muted text-xs">Exactly what {selectedLead?.email} will receive</p>
            </div>
            <div className="preview-mode-toggle">
              <button
                type="button"
                className={`btn btn-xs ${previewMode === "rendered" ? "btn-primary" : "btn-ghost"}`}
                onClick={() => setPreviewMode("rendered")}
              >
                Rendered HTML
              </button>
              <button
                type="button"
                className={`btn btn-xs ${previewMode === "text" ? "btn-primary" : "btn-ghost"}`}
                onClick={() => setPreviewMode("text")}
              >
                Plain Text
              </button>
            </div>
          </div>

          {/* Missing variables alert */}
          {allUnfilled.length > 0 && (
            <div className="alert alert-warning my-2 text-xs">
              <strong>Note:</strong> Some variables are still unfilled:{" "}
              {allUnfilled.map((v) => (
                <code key={v} className="ml-1">
                  {"{{"}
                  {v}
                  {"}}"}
                </code>
              ))}
            </div>
          )}

          {/* Email Preview Frame */}
          <div className="email-preview-envelope mt-3">
            <div className="email-meta-strip">
              <div className="email-meta-item">
                <span className="email-meta-label">From:</span>
                <span className="email-meta-value">
                  CurrencyCore &lt;{senderEmail}&gt;
                </span>
              </div>
              <div className="email-meta-item">
                <span className="email-meta-label">Reply-To:</span>
                <span className="email-meta-value">{senderEmail}</span>
              </div>
              <div className="email-meta-item">
                <span className="email-meta-label">To:</span>
                <span className="email-meta-value font-semibold">
                  {contactName.trim() ? `${contactName} ` : ""}
                  &lt;{selectedLead?.email}&gt;
                </span>
              </div>
              <div className="email-meta-item">
                <span className="email-meta-label">Subject:</span>
                <span className="email-meta-value font-medium text-white">
                  {interpolatedSubject || "(No subject)"}
                </span>
              </div>
            </div>

            <div className="email-body-canvas">
              {previewMode === "rendered" ? (
                <div
                  className="email-rendered-html prose"
                  dangerouslySetInnerHTML={{ __html: interpolatedHtml }}
                />
              ) : (
                <pre className="email-rendered-plain">{interpolatedText}</pre>
              )}
            </div>
          </div>

          {/* Dispatch Bar */}
          <div className="lead-dispatch-bar mt-4 pt-4 border-t">
            <div className="dispatch-info">
              <span className="dispatch-info-main">Manual 1-by-1 Send</span>
              <span className="dispatch-info-sub">
                Dispatches immediately from <span className="mono">{senderEmail}</span>
              </span>
            </div>
            <button
              type="button"
              className="btn btn-primary btn-lg"
              disabled={sending || !selectedLead}
              onClick={handleSendEmail}
            >
              {sending ? (
                <>
                  <span className="spinner" /> Sending...
                </>
              ) : (
                <>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M22 2L11 13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                  Send Email to {selectedLead?.company || "Lead"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
